import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { CalendarDays, Download, Link, ListOrdered, ListVideo, Search, Server, Tv, Zap } from 'lucide-react';
import { usePermissions, useTranslation } from '@/hooks';
import { useBranding } from '@/context/BrandingContext';

interface TVChannelsLayoutProps {
  children: ReactNode;
}

const TVChannelsLayout = ({ children }: TVChannelsLayoutProps) => {
  const { t } = useTranslation();
  const { branding } = useBranding();
  const { hasAnyPermission } = usePermissions();
  const modules = branding.modules ?? {};
  const showIptvProviders = modules.iptvProviders === true;
  const showFlussonicServers = modules.flussonicServers === true;
  const showChannelAutomations = modules.channelAutomations === true;
  const showChannelScraping = modules.channelScraping === true;
  const showGuide = modules.tvGuide === true && hasAnyPermission(['TVGUIDE_READ', 'TVGUIDE_CREATE', 'TVGUIDE_UPDATE', 'TVGUIDE_DELETE']);

  const navItems = [
    { icon: Tv, label: 'Lista de Canales', mobileLabel: 'Canales', to: '/content/tv-channels', end: true },
    { icon: Link, label: 'URL Base de Canales', mobileLabel: 'URL Base', to: '/content/tv-channels/base-url' },
    ...(showGuide
      ? [{ icon: CalendarDays, label: 'Guías de Programación', mobileLabel: 'Guías', to: '/content/tv-channels/tv-guide' }]
      : []),
    { icon: ListOrdered, label: 'Organizar Canales', mobileLabel: 'Organizar', to: '/content/tv-channels/organize' },
    { icon: Download, label: 'Importar Listas IPTV', mobileLabel: 'Importar', to: '/content/tv-channels/import' },
    ...(showIptvProviders
      ? [{ icon: ListVideo, label: 'Proveedores de Listas', mobileLabel: 'Listas', to: '/content/tv-channels/providers' }]
      : []),
    ...(showFlussonicServers
      ? [{ icon: Server, label: 'Servidores Flussonic', mobileLabel: 'Flussonic', to: '/content/tv-channels/flussonic-servers' }]
      : []),
    ...(showChannelAutomations
      ? [{ icon: Zap, label: 'Automatizaciones', mobileLabel: 'Auto', to: '/content/tv-channels/automations' }]
      : []),
    ...(showChannelScraping
      ? [{ icon: Search, label: 'Scraping de Canales', mobileLabel: 'Scraping', to: '/content/tv-channels/scraping/tasks' }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full p-4">
        <div className="sticky top-0 z-20 -mx-4 mb-4 bg-gray-50 px-4 sm:-mx-6 sm:px-6 lg:hidden">
          <div className="overflow-hidden bg-white shadow">
            <nav className="flex overflow-x-auto" aria-label="TV channels sections">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex flex-1 items-center justify-center px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                {item.mobileLabel}
              </NavLink>
            ))}
            </nav>
          </div>
        </div>
        <div className="flex flex-col gap-4 lg:flex-row">
          <aside className="hidden w-full shrink-0 lg:block lg:w-64">
            <div className="border border-gray-200 bg-white p-3">
              <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {t.tvChannels.title}
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`
                    }
                  >
                    <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </aside>
          <section className="min-w-0 flex-1">{children}</section>
        </div>
      </div>
    </div>
  );
};

export default TVChannelsLayout;
